'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { X, GitCompare, Search, Plus } from 'lucide-react';
import { SCHOOLS } from '@/data/schools';
import type { School } from '@/data/schools';
import KhdaBadge from '@/components/ui/KhdaBadge';
import CurriculumBadge from '@/components/ui/CurriculumBadge';

const STORAGE_KEY = 'schoolmapper_compare_list';

// Compact KHDA pill for mobile — no "KHDA:" prefix, just the rating value
const KhdaPill = ({ rating }: { rating: School['khdaRating'] }) => {
  const config: Record<string, { bg: string; text: string; dot: string }> = {
    Outstanding:       { bg: 'bg-purple-100', text: 'text-purple-800', dot: 'bg-purple-700' },
    'Very Good':       { bg: 'bg-green-100',  text: 'text-green-800',  dot: 'bg-green-700'  },
    Good:              { bg: 'bg-orange-100', text: 'text-orange-800', dot: 'bg-orange-600' },
    Acceptable:        { bg: 'bg-sky-100',    text: 'text-sky-800',    dot: 'bg-sky-600'    },
    Weak:              { bg: 'bg-orange-100', text: 'text-orange-800', dot: 'bg-orange-700' },
    'Very Weak':       { bg: 'bg-red-100',    text: 'text-red-800',    dot: 'bg-red-700'    },
    'Not yet inspected':{ bg: 'bg-gray-100',  text: 'text-gray-600',   dot: 'bg-gray-500'   },
  };
  const c = config[rating] ?? config['Not yet inspected'];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${c.bg} ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${c.dot}`} />
      {rating}
    </span>
  );
};

const COMPARE_ROWS: {
  key: string;
  label: string;
  render: (s: School) => React.ReactNode;
  mobileRender?: (s: School) => React.ReactNode;
}[] = [
  {
    key: 'location',
    label: 'Location',
    render: (s) => <span className="text-sm text-foreground">{s.location}</span>,
    mobileRender: (s) => <span className="text-xs text-foreground leading-snug">{s.location}</span>,
  },
  {
    key: 'khda',
    label: 'KHDA Rating',
    render: (s) => <KhdaBadge rating={s.khdaRating} size="sm" />,
    mobileRender: (s) => <KhdaPill rating={s.khdaRating} />,
  },
  {
    key: 'curriculum',
    label: 'Curriculum',
    render: (s) => <CurriculumBadge curriculum={s.curriculum} size="sm" />,
    mobileRender: (s) => <CurriculumBadge curriculum={s.curriculum} size="sm" />,
  },
  {
    key: 'fee-min',
    label: 'Min Annual Fee',
    render: (s) => (
      <span className="tabular-nums font-semibold text-sm">
        {s.feeRangeMin === 0 ? 'Non-fee paying' : `AED ${s.feeRangeMin.toLocaleString()}`}
      </span>
    ),
    mobileRender: (s) => (
      <span className="tabular-nums font-semibold text-xs text-foreground whitespace-nowrap">
        {s.feeRangeMin === 0 ? 'Non-fee paying' : `AED ${s.feeRangeMin.toLocaleString()}`}
      </span>
    ),
  },
  {
    key: 'fee-max',
    label: 'Max Annual Fee',
    render: (s) => (
      <span className="tabular-nums font-semibold text-sm">
        {s.feeRangeMax === 0 ? 'N/A' : `AED ${s.feeRangeMax.toLocaleString()}`}
      </span>
    ),
    mobileRender: (s) => (
      <span className="tabular-nums font-semibold text-xs text-foreground whitespace-nowrap">
        {s.feeRangeMax === 0 ? 'N/A' : `AED ${s.feeRangeMax.toLocaleString()}`}
      </span>
    ),
  },
  {
    key: 'age-range',
    label: 'Age Range',
    render: (s) => <span className="text-sm text-foreground">{s.ageRange}</span>,
    mobileRender: (s) => <span className="text-xs text-foreground">{s.ageRange}</span>,
  },
];

export default function SchoolComparisonClient() {
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const [mounted, setMounted] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) setCompareIds(parsed);
      } catch {
        // ignore
      }
    }

    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          if (Array.isArray(parsed)) setCompareIds(parsed);
        } catch {
          // ignore
        }
      }
    };
    window.addEventListener('storage', handleStorage);

    const handleCustom = () => {
      const stored2 = localStorage.getItem(STORAGE_KEY);
      if (stored2) {
        try {
          const parsed = JSON.parse(stored2);
          if (Array.isArray(parsed)) setCompareIds(parsed);
        } catch {
          // ignore
        }
      }
    };
    window.addEventListener('schoolmapper_compare_updated', handleCustom);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('schoolmapper_compare_updated', handleCustom);
    };
  }, []);

  const schools = compareIds
    .map((id) => SCHOOLS.find((s) => s.id === id))
    .filter(Boolean) as School[];

  const handleRemove = (id: string) => {
    const next = compareIds.filter((x) => x !== id);
    setCompareIds(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    window.dispatchEvent(new Event('schoolmapper_compare_updated'));
  };

  const handleClearAll = () => {
    setCompareIds([]);
    localStorage.setItem(STORAGE_KEY, JSON.stringify([]));
    window.dispatchEvent(new Event('schoolmapper_compare_updated'));
  };

  if (!mounted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (schools.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 py-20 text-center">
        <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
          <GitCompare size={36} className="text-primary" />
        </div>
        <h2 className="font-display font-bold text-2xl text-foreground mb-3">No Schools Selected</h2>
        <p className="text-muted-foreground text-base max-w-md mb-8">
          You haven&apos;t added any schools to compare yet. Browse schools and click{' '}
          <strong>&ldquo;Compare&rdquo;</strong> on any school card to add it here.
        </p>
        <Link
          href="/school-search-discovery"
          className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl font-semibold text-sm hover:bg-primary/90 transition-all active:scale-95"
        >
          <Search size={16} />
          Browse Schools
        </Link>
      </div>
    );
  }

  // ─── Mobile: horizontally scrollable table ───────────────────────────────
  const CRITERIA_WIDTH = 110; // px — sticky left column
  const SCHOOL_COL_WIDTH = 200; // px — each school column

  const MobileView = () => (
    <div className="space-y-3">
      {/* Scrollable comparison table */}
      <div
        ref={scrollRef}
        className="overflow-x-auto overflow-y-visible rounded-2xl border border-border shadow-sm bg-white"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {/* Min-width forces horizontal scroll when schools overflow */}
        <div style={{ minWidth: CRITERIA_WIDTH + schools.length * SCHOOL_COL_WIDTH }}>

          {/* ── School header row (sticky top) ── */}
          <div
            className="flex border-b border-border bg-slate-50 sticky top-0 z-20"
          >
            {/* Criteria label cell — sticky left */}
            <div
              className="shrink-0 flex items-end px-3 py-3 border-r border-border bg-slate-50 sticky left-0 z-30"
              style={{ width: CRITERIA_WIDTH }}
            >
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Criteria</span>
            </div>

            {/* School header cells */}
            {schools.map((school) => (
              <div
                key={`mob-hdr-${school.id}`}
                className="shrink-0 flex flex-col px-3 py-3 border-r border-border last:border-r-0"
                style={{ width: SCHOOL_COL_WIDTH }}
              >
                {/* School name + remove button */}
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <p className="font-bold text-[13px] text-slate-800 leading-snug break-words min-w-0">
                    {school.shortName}
                  </p>
                  <button
                    onClick={() => handleRemove(school.id)}
                    className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full hover:bg-red-50 hover:text-red-500 text-slate-400 transition-colors mt-0.5"
                    title={`Remove ${school.shortName}`}
                    aria-label={`Remove ${school.shortName}`}
                  >
                    <X size={13} />
                  </button>
                </div>
                {/* View profile link */}
                <Link
                  href={`/school-profile-comparison?school=${school.id}`}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline"
                >
                  View profile →
                </Link>
              </div>
            ))}
          </div>

          {/* ── Comparison rows ── */}
          {COMPARE_ROWS.map((row, i) => (
            <div
              key={`mob-row-${row.key}`}
              className={`flex border-b border-border last:border-b-0 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}`}
            >
              {/* Criteria label — sticky left */}
              <div
                className={`shrink-0 flex items-center px-3 py-3 border-r border-border sticky left-0 z-10 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}`}
                style={{ width: CRITERIA_WIDTH }}
              >
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider leading-tight">
                  {row.label}
                </span>
              </div>

              {/* School data cells */}
              {schools.map((school) => (
                <div
                  key={`mob-cell-${row.key}-${school.id}`}
                  className="shrink-0 flex items-center px-3 py-3 border-r border-border last:border-r-0"
                  style={{ width: SCHOOL_COL_WIDTH }}
                >
                  {(row.mobileRender ?? row.render)(school)}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Scroll hint — only shown when there are 2+ schools */}
      {schools.length >= 2 && (
        <p className="text-center text-[11px] text-slate-400 flex items-center justify-center gap-1">
          <span>←</span>
          <span>Swipe to compare schools</span>
          <span>→</span>
        </p>
      )}
    </div>
  );

  // ─── Desktop view (original layout, unchanged) ───────────────────────────
  const DesktopView = () => (
    <div>
      <div className="bg-white border border-border rounded-2xl overflow-hidden shadow-sm">
        <div
          className="grid border-b border-border bg-muted/30"
          style={{ gridTemplateColumns: `200px repeat(${schools.length}, minmax(160px, 1fr))` }}
        >
          <div className="px-5 py-4 flex items-center">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Criteria</span>
          </div>
          {schools.map((school) => (
            <div key={`header-${school.id}`} className="px-4 py-4 border-l border-border">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-display font-bold text-sm text-foreground leading-tight">{school.shortName}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">{school.community}</p>
                </div>
                <button
                  onClick={() => handleRemove(school.id)}
                  className="p-1 rounded-lg hover:bg-red-50 hover:text-red-500 text-muted-foreground transition-colors shrink-0"
                  title={`Remove ${school.shortName}`}
                >
                  <X size={14} />
                </button>
              </div>
              <Link
                href={`/school-profile-comparison?school=${school.id}`}
                className="mt-2 inline-block text-xs text-primary hover:underline font-medium"
              >
                View full profile →
              </Link>
            </div>
          ))}
        </div>

        {COMPARE_ROWS.map((row, i) => (
          <div
            key={`row-${row.key}`}
            className={`grid border-b border-border last:border-b-0 ${i % 2 === 0 ? 'bg-white' : 'bg-muted/10'}`}
            style={{ gridTemplateColumns: `200px repeat(${schools.length}, minmax(160px, 1fr))` }}
          >
            <div className="px-5 py-4 flex items-center border-r border-border">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{row.label}</span>
            </div>
            {schools.map((school) => (
              <div key={`cell-${row.key}-${school.id}`} className="px-4 py-4 border-l border-border flex items-center">
                {row.render(school)}
              </div>
            ))}
          </div>
        ))}
      </div>

      <div
        className="mt-4 grid gap-3"
        style={{ gridTemplateColumns: `200px repeat(${schools.length}, minmax(160px, 1fr))` }}
      >
        <div />
        {schools.map((school) => (
          <Link
            key={`cta-${school.id}`}
            href={`/school-profile-comparison?school=${school.id}`}
            className="flex items-center justify-center py-3 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary/90 transition-all active:scale-95"
          >
            View {school.shortName}
          </Link>
        ))}
      </div>
    </div>
  );

  return (
    <div className="max-w-screen-2xl mx-auto px-3 sm:px-6 lg:px-8 xl:px-10 py-4 sm:py-8">

      {/* ── Header ── */}
      {/* Mobile header: stacked layout to prevent collision */}
      <div className="mb-4 sm:mb-6 md:hidden">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="min-w-0">
            <h1 className="font-display font-bold text-lg text-foreground leading-tight">School Comparison</h1>
            <p className="text-slate-500 text-xs mt-0.5">
              {schools.length} school{schools.length !== 1 ? 's' : ''} selected
            </p>
          </div>
          {/* Clear All — top right */}
          <button
            onClick={handleClearAll}
            className="shrink-0 px-3 py-2 rounded-xl border border-red-200 text-xs font-medium text-red-500 hover:bg-red-50 transition-all active:scale-95"
          >
            Clear All
          </button>
        </div>
        {/* Add Schools — full-width below */}
        <Link
          href="/school-search-discovery"
          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-border text-xs font-medium text-slate-600 hover:text-foreground hover:bg-muted transition-all w-full justify-center"
        >
          <Plus size={13} />
          Add Schools
        </Link>
      </div>

      {/* Desktop header: original single-row layout */}
      <div className="hidden md:flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl text-foreground">School Comparison</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {schools.length} school{schools.length !== 1 ? 's' : ''} selected · up to 10 schools
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            href="/school-search-discovery"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
          >
            <Plus size={13} />
            Add Schools
          </Link>
          <button
            onClick={handleClearAll}
            className="px-4 py-2.5 rounded-xl border border-red-200 text-sm font-medium text-red-500 hover:bg-red-50 transition-all"
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Mobile view (< md) */}
      <div className="md:hidden">
        <MobileView />
      </div>

      {/* Desktop view (>= md) */}
      <div className="hidden md:block">
        <DesktopView />
      </div>
    </div>
  );
}
